import { NotificationType } from '@gearbelt/global';
import { queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';

export const legacyComplianceNotificationKeys = {
  all: ['legacy-compliance-notifications'] as const,
};

export const issueNotificationRuleKeys = {
  all: ['issue-notification-rules'] as const,
  byType: (type: NotificationType) => [...issueNotificationRuleKeys.all, type] as const,
};

export const scheduleNotificationRuleKeys = {
  all: ['schedule-notification-rules'] as const,
  detail: (ruleId: string | null | undefined) => [...scheduleNotificationRuleKeys.all, 'detail', ruleId] as const,
};

export const legacyInspectionNotificationKeys = {
  all: ['legacy-inspection-notifications'] as const,
};

export const workOrderNotificationKeys = {
  all: ['work-order-notifications'] as const,
};

export const getLegacyComplianceNotificationsQueryOptions = () => {
  return queryOptions({
    queryKey: legacyComplianceNotificationKeys.all,
    queryFn: async () => {
      const [notificationsData, rulesData] = await Promise.all([
        (await ApiService.GetNotifications()).json(),
        (await ApiService.GetNotificationRules({ type: NotificationType.Compliance })).json(),
      ]);
      const emails = notificationsData.notifications.filter((n) => n.type === NotificationType.Compliance).map((n) => n.email);
      const rules = rulesData.rules;
      return { emails, rules };
    },
  });
};

export const getIssueNotificationRulesQueryOptions = ({ type }: { type: NotificationType }) => {
  return queryOptions({
    queryKey: issueNotificationRuleKeys.byType(type),
    queryFn: async () => {
      const allRules = (await (await ApiService.GetIssueNotificationRules()).json()).rules;
      return allRules.filter((rule) => rule.notificationType === type);
    },
  });
};

export const getScheduleNotificationRulesQueryOptions = () => {
  return queryOptions({
    queryKey: scheduleNotificationRuleKeys.all,
    queryFn: async () => (await ApiService.GetScheduleNotificationRules({})).json(),
  });
};

export const getLegacyInspectionNotificationsQueryOptions = () => {
  return queryOptions({
    queryKey: legacyInspectionNotificationKeys.all,
    queryFn: async () => (await (await ApiService.GetNotifications()).json()) as { inspection: boolean },
  });
};

export const getWorkOrderNotificationsQueryOptions = () => {
  return queryOptions({
    queryKey: workOrderNotificationKeys.all,
    queryFn: async () => (await ApiService.GetNotifyWorkOrder()).json(),
  });
};

export const getScheduleNotificationRuleQueryOptions = ({ ruleId }: { ruleId: string | null | undefined }) => {
  return queryOptions({
    queryKey: scheduleNotificationRuleKeys.detail(ruleId),
    queryFn: async () => {
      if (!ruleId) throw new Error('ruleId is required');
      return (await (await ApiService.GetScheduleNotificationRuleById(ruleId)).json()).scheduleNotificationRule;
    },
    enabled: !!ruleId,
  });
};
